import { useDispatch, useSelector } from "react-redux";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSnackbar } from "notistack";
import { Field, Formik } from "formik";
import { Link, useHistory } from "react-router-dom";

import { ModalBody, ModalFooter } from "application/modal";
import { BtnType, Button } from "modules/primitives";
import { useModelPopup } from "../../../common/hooks";
import styles from "modules/projects/project/manageProject/details/details.module.scss";
import { getPlantsAndAreas } from "modules/admin/adminPlantsAndAreas/adminPlantsAndAreas.actions";
import { getPlantByType } from "../../actions/common.action";
import PlantMultiSelect from "../plantMultiselect";
import { Icon, smSize } from "../../icon";
import PlantSelectWithOptions from './plantSelectWithOptions';
import { httpPut } from "../../httpCall";

const ForPossibleReplicationsModal = ({ handleSave, instance, replicationsData, data }) => {
  const history = useHistory();
  const popup = useModelPopup();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const formRef = useRef(null);

  const [isAdmin, setIsAdmin] = useState(false);
  const [spocPlant, setSpocPlant] = useState({
    state: 'under_review'
  });

  useEffect(() => {
    const council_role = localStorage.getItem("council_role");
    setIsAdmin(
      council_role === "ttp_administrator" ||
      council_role === "council_administrator"
    );
    return () => null;
  }, []);

  const {
    authReducer: { session },
    councilReducer: { selectedCouncil },
    ideaReducer: { ideaPlant },
    adminPlantsAndAreasReducer: { plantsAndAreas }
  } = useSelector((state) => state);

  const [model, setModel] = useState({});

  useEffect(() => {
    if (!plantsAndAreas.length && selectedCouncil?.name === 'Ford') {
      dispatch(
        getPlantsAndAreas({
          enqueueSnackbar,
        })
      );
    }
  }, [plantsAndAreas, selectedCouncil]);

  useEffect(() => {
    if (!ideaPlant) {
      dispatch(getPlantByType({
        resourceId: data?.id,
        enqueueSnackbar,
        type: instance
      }));
    }
  }, []);

  const getSpocPlant = useMemo(() => replicationsData?.find(plant => plant.current_user_plant), [replicationsData]);

  const getPossiblePlantsFromSource = useCallback((data) => {
    return plantsAndAreas
      .filter(p => data.some(psPlant => psPlant.id === p.id))
      .map(p => ({...p, ...p.attributes}));
  }, [plantsAndAreas]);

  useEffect(() => {
    if (!isAdmin && (session.spoc || session.innovation_leader) && getSpocPlant) {
      setSpocPlant({
        ...getSpocPlant,
        state: getSpocPlant.state || 'under_review',
      });
    }
  }, [session, isAdmin, getSpocPlant]);

  useEffect(() => {
    if (replicationsData && plantsAndAreas?.length) {
      const otherPropsById = replicationsData.reduce((acc, elem) => {
        return {
          ...acc,
          [elem.id]: {
            selectedAreas: elem.areas?.map(a => ({...a, area_id: a.id, area_name: a.name})) || [],
            otherOptions: elem.state || 'under_review',
          }
        }
      }, {}) || {};
      const selectedPlants = getPossiblePlantsFromSource(replicationsData)

      const newModel = {
        selectedPlant1: selectedPlants,
        originalPlants: selectedPlants,
        ...otherPropsById,
      };

      setModel(newModel);
    }
  }, [replicationsData, plantsAndAreas])

  const handleCancelClick = () => {
    popup.hide();
  };

  const getPlantsAsOptions = useMemo(() => {
    const plantsArray = plantsAndAreas.map(plant => ({
      id: plant.id,
      name: plant.attributes.name,
      areas: plant.attributes.areas,
    }));

    if (data?.replications?.length || data?.original_project?.plant) {
      const replicationIds = data?.replications.map(p => p.plant?.id);
      const areaIdsFromOriginalProj = data?.original_project?.plant?.areas
        ? data?.original_project?.plant.areas.map(a => a.id)
        : [];

      return plantsArray
        .filter(p => !replicationIds.includes(p.id) && data?.original_project?.plant?.id !== p.id)
        .map(p => {
          if (!areaIdsFromOriginalProj.length) return p;

          return {
            ...p,
            areas: p.areas.filter(a => !areaIdsFromOriginalProj.includes(a.area_id)),
          }
        })
    }

    return plantsArray;
  }, [plantsAndAreas, data]);

  const handleSubmit = () => {
    const values = formRef.current.values;
    let possible_replications = [];

    if ((session.spoc || session.innovation_leader) && spocPlant && !isAdmin) {
      possible_replications = values.selectedPlant1.map(plant => {
        const isCurrentUserPlant = replicationsData.find(p => p.id === plant.id && p.current_user_plant);

        if (isCurrentUserPlant) {
          return {
            plant_id: plant.id,
            state: spocPlant.state,
            area_ids: spocPlant.areas?.map(a => a.area_id || a.id) || [],
          }
        }

        const getPlantSelectedValues = values[plant.id];

        return {
          plant_id: plant.id,
          state: getPlantSelectedValues.otherOptions,
          area_ids: getPlantSelectedValues.selectedAreas?.map(a => a.area_id || a.id) || [],
        }
      });
    } else {
      let data = values.selectedPlant1;

      // some kind of detect delete plants
      if (values.selectedPlant1.length < values.originalPlants.length) {
        data = values.originalPlants.map(plant => {
          const getPlant = values.selectedPlant1.find(p => p.id === plant.id);

          if (getPlant) {
            return getPlant;
          }

          return {
            ...plant,
            discarded_at: new Date(),
          }
        });
      }

      possible_replications = data.map(plant => {
        const getPlantSelectedValues = values[plant.id];

        return {
          discarded_at: plant.discarded_at,
          plant_id: plant.id,
          state: getPlantSelectedValues.otherOptions,
          area_ids: getPlantSelectedValues.selectedAreas?.map(a => a.area_id || a.id) || [],
        }
      });
    }

    const payload = {
      resource_id: data.id,
      resource_type: instance,
      possible_replications,
    };

    httpPut({
      call: `possible_replications/plants_and_areas/batch_update`,
      data: payload
    }).subscribe(res => {
      enqueueSnackbar("Successfully updated possible replications", {
        variant: "success",
      });
      popup.hide();
      handleSave(res.response);
    })
  };

  const makeSaveButton = useCallback(() => {
    if (isAdmin || ((session.spoc || session.innovation_leader) && getSpocPlant)) {
      return (
        <Button type="submit" btn={BtnType.REGULAR} onClick={handleSubmit}>
          Submit
        </Button>
      )
    }

    return null
  }, [session, handleSubmit]);

  const handleSpocPlantDelete = useCallback(() => {
    const newPlants = formRef.current.values.selectedPlant1
      .filter(plant => plant.id !== spocPlant.id);

    formRef.current.setFieldValue("selectedPlant1", newPlants);

    setSpocPlant(null);
  }, [spocPlant, formRef]);

  const spocAreaExistsInPossibleAreas = useCallback((area) => {
    if (!spocPlant?.state?.length) {
      return false;
    }

    return spocPlant?.areas?.some(a => (a.area_id || a.id) === (area.area_id || area.id));
  }, [spocPlant, model]);

  const handleSpocAreaDelete = useCallback((area) => {
    const newSpoc = {
      ...spocPlant,
      areas: spocPlant?.areas?.filter(a => (a.area_id || a.id) !== (area.area_id || area.id)) || [],
    };

    setSpocPlant(newSpoc);
  }, [spocPlant, formRef]);

  const handleRedirectToSettings = (evt) => {
    evt.preventDefault();
    history.push('/settings');
    popup.hide();
  }

  return (
    <div>
      <ModalBody>
        <div className={styles.formContainer}>
          <Formik
            innerRef={formRef}
            initialValues={model}
            enableReinitialize
            onSubmit={() => {}}
          >
            {(formikprops) => (
              <>
                {
                  isAdmin && (
                    <div>
                      <PlantMultiSelect
                        formikprops={formikprops}
                        plants={getPlantsAsOptions}
                        hideAreasSelect
                      />
                      <div className="mt-3">
                        {
                          formikprops.values.selectedPlant1?.map((plant, index) => (
                            <PlantSelectWithOptions
                              key={plant.id}
                              plant={plant}
                              formikprops={formikprops}
                              index={index + 1}
                            />
                          ))
                        }
                      </div>
                    </div>
                  )
                }
                {
                  !isAdmin && (session.spoc || session.innovation_leader) && (
                    <>
                      {
                        !getSpocPlant ? (
                          <Link to="#" onClick={handleRedirectToSettings}>Please add your Plant in Settings</Link>
                        ) : (
                          <div>
                            <span className="font-weight-bold mb-3">Plant</span>
                            <div className="d-flex align-items-center mb-3">
                              <span>{getSpocPlant.name}</span>
                              {
                                spocPlant?.state?.length ? (
                                  <Icon
                                    {...smSize}
                                    icon="icn-button-delete"
                                    className="ml-3 cursor-pointer"
                                    onClick={() => handleSpocPlantDelete()}
                                  />
                                ) : null
                              }
                            </div>
                            {
                              getSpocPlant?.areas.length ? (
                                <>
                                  <span className="font-weight-bold mb-3">Plant Areas</span>
                                  {
                                    getSpocPlant.areas.map(area => (
                                      <div className="d-flex align-items-center" key={area.id}>
                                        <span>{area.name}</span>
                                        {
                                          spocAreaExistsInPossibleAreas(area) && (
                                            <Icon
                                              {...smSize}
                                              icon="icn-button-delete"
                                              className="ml-3 cursor-pointer"
                                              onClick={() => handleSpocAreaDelete(area)}
                                            />
                                          )
                                        }
                                      </div>
                                    ))
                                  }
                                </>
                              ) : null
                            }
                            <div className="form-check mt-3">
                              <label className="form-check-label d-flex align-items-center">
                                <Field
                                  className="form-check-input"
                                  type="radio"
                                  name="otherOptions_spoc"
                                  value="under_review"
                                  onChange={() => {
                                    setSpocPlant({
                                      ...spocPlant || {},
                                      state: 'under_review'
                                    });
                                  }}
                                  checked={spocPlant?.state === 'under_review'}
                                />
                                Possible Replication
                              </label>
                            </div>
                            <div className="form-check">
                              <label className="form-check-label d-flex align-items-center">
                                <Field
                                  className="form-check-input"
                                  type="radio"
                                  name="otherOptions_spoc"
                                  value="different_solution"
                                  onChange={() => {
                                    setSpocPlant({
                                      ...spocPlant || {},
                                      state: 'different_solution'
                                    });
                                  }}
                                  checked={spocPlant?.state === 'different_solution'}
                                />
                                Different Solution in Place
                              </label>
                            </div>
                            <div className="form-check">
                              <label className="form-check-label d-flex align-items-center">
                                <Field
                                  className="form-check-input"
                                  type="radio"
                                  name="otherOptions_spoc"
                                  value="not_applicable"
                                  onChange={() => {
                                    setSpocPlant({
                                      ...spocPlant || {},
                                      state: 'not_applicable'
                                    });
                                  }}
                                  checked={spocPlant?.state === 'not_applicable'}
                                />
                                Not Applicable for this Plant
                              </label>
                            </div>
                          </div>
                        )
                      }
                    </>
                  )
                }
              </>
            )}
          </Formik>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button btn={BtnType.FRAME_LESS} onClick={handleCancelClick}>
          Cancel
        </Button>
        {makeSaveButton()}
      </ModalFooter>
    </div>
  );
};
export default ForPossibleReplicationsModal;
